<script setup lang="ts">

import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, numeric } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{
  disable?: boolean
  hideHeader?: boolean
  subHeader?: string
  bankValidation?: { valid: boolean; errorMessage: string } | null
  runBankCheck?: (u: any) => Promise<{ valid: boolean; errorMessage?: string }>
}>()

const localBankError = ref('')
const showBankError = ref(false)

const rules = {
  umsokn: {
    bankNumber: { required, minLength: minLength(4), maxLength: maxLength(4), numeric },
    ledger: { required, numeric },
    accountNumber: { required, numeric, minLength: minLength(1) }
  }
}

const v$ = useVuelidate(rules, { umsokn })

function digitsOnly(v: string) {
  return (v || '').replace(/\D/g, '')
}

onLoaded(() => {
  if (!umsokn.value) return

  if (!umsokn.value.childPension) umsokn.value.childPension = []
  if (!umsokn.value.anotherPensions) umsokn.value.anotherPensions = []
  if (!umsokn.value.healthDoctors) umsokn.value.healthDoctors = []
  if (!umsokn.value.healthorganizations) umsokn.value.healthorganizations = []
  if (!umsokn.value.career) umsokn.value.career = []
  
})

async function validate(): Promise<boolean> {
  localBankError.value = ''
  showBankError.value = false
  v$.value.$touch()
  if (v$.value.$invalid) return false

  if (props.runBankCheck) {
    const res = await props.runBankCheck(umsokn.value)
    if (!res.valid) {
      localBankError.value = res.errorMessage || t('umsokn_bankareikningur_villa')
      showBankError.value = true
      return false
    }
  }
  return true
}

defineExpose({ v$, validate })
</script>

<template>
  <div v-if="umsokn">
    <v-row v-if="!props.hideHeader">
      <v-col>
        <h2 class="forms__header">
          {{ t('umsokn_bankareikningur_header1') }}
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <!-- Bank number -->
      <v-col cols="12" md="4" lg="5">
        <v-text-field
          v-model="umsokn.bankNumber"
          :label="t('umsokn_bankareikningur_banki')"
          variant="outlined"
          :readonly="props.disable"
          type="tel"
          inputmode="numeric"
          @update:model-value="v => (umsokn.bankNumber = digitsOnly(v))"
          :error="v$.umsokn.bankNumber?.$error"
          :error-messages="[
            v$.umsokn.bankNumber?.required?.$invalid ? t('umsokn_bankareikningur_banki_error') : '',
            v$.umsokn.bankNumber?.minLength?.$invalid || v$.umsokn.bankNumber?.maxLength?.$invalid
              ? t('umsokn_bankareikningur_banki_error_length')
              : '',
            v$.umsokn.bankNumber?.numeric?.$invalid ? t('umsokn_bankareikningur_banki_error_numeric') : ''
          ].filter(Boolean)"
        />
      </v-col>

      <!-- Ledger -->
      <v-col cols="12" md="4" lg="2">
        <v-text-field
          v-model="umsokn.ledger"
          :label="t('umsokn_bankareikningur_hofudbok')"
          variant="outlined"
          :readonly="props.disable"
          type="tel"
          inputmode="numeric"
          @update:model-value="v => (umsokn.ledger = digitsOnly(v))"
          :error="v$.umsokn.ledger?.$error"
          :error-messages="[
            v$.umsokn.ledger?.required?.$invalid ? t('umsokn_bankareikningur_hofudbok_error') : '',
            v$.umsokn.ledger?.numeric?.$invalid ? t('umsokn_bankareikningur_hofudbok_error_numeric') : ''
          ].filter(Boolean)"
        />
      </v-col>

      <!-- Account number -->
      <v-col cols="12" md="4" lg="5">
        <v-text-field
          v-model="umsokn.accountNumber"
          :label="t('umsokn_bankareikningur_reikningsnumer')"
          variant="outlined"
          :readonly="props.disable"
          type="tel"
          inputmode="numeric"
          @update:model-value="v => (umsokn.accountNumber = digitsOnly(v))"
          :error="v$.umsokn.accountNumber?.$error"
          :error-messages="[
            v$.umsokn.accountNumber?.required?.$invalid ? t('umsokn_bankareikningur_reikningsnumer_error') : '',
            v$.umsokn.accountNumber?.numeric?.$invalid ? t('umsokn_bankareikningur_reikningsnumer_error_numeric') : ''
          ].filter(Boolean)"
        />
      </v-col>
    </v-row>

    <!-- Extra bank validation error -->
    <v-row v-if="showBankError || (props.bankValidation && props.bankValidation.valid === false)">
      <v-col>
        <v-alert type="error" color="error" variant="flat" density="compact" class="mt-2" closable @click:close="showBankError = false">
          {{ localBankError || props.bankValidation?.errorMessage }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row v-if="props.subHeader">
      <v-col>
        <p class="forms__paragraph">{{ props.subHeader }}</p>
      </v-col>
    </v-row>
  </div>
</template>
